import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getLevelChipClass } from "../utils/levelColors";

const API_URL = process.env.REACT_APP_API_URL;

const directionLabels = {
  up: "Lên hạng",
  down: "Xuống hạng",
  same: "Thay đổi",
};

const directionVariants = {
  up: "success",
  down: "danger",
  same: "secondary",
};

const getDirectionIcon = (direction) => {
  if (direction === "up") return "↑";
  if (direction === "down") return "↓";
  return "•";
};

const getFirstTeamName = (teamNames) => {
  return String(teamNames || "Chưa có đội").split(",")[0].trim();
};

const getSerieLabel = (levelId, levelName) => {
  const normalizedLevelName = String(levelName || "").trim();
  if (Number(levelId) === 2 || normalizedLevelName === "Serie E") return "Serie B";
  if (Number(levelId) === 4) return "Serie C";
  if (normalizedLevelName) return normalizedLevelName;
  return `Serie ${levelId || "khác"}`;
};

const getChangeSerieLabels = (change) =>
  (change.series || []).map((serie) => getSerieLabel(serie.id, serie.name));

const RankChangesPanel = ({ compact = false, seasonId = null, showAllInSeason = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roundFilter, setRoundFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [serieFilter, setSerieFilter] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchRankChanges = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (seasonId) params.set("season_id", seasonId);
        // Mua da ket thuc: lay toan bo thay doi trong mua (scope=all).
        // Mua dang dien ra: giu hanh vi cu (vong moi nhat hoan thanh + loc vong).
        if (seasonId && showAllInSeason) params.set("scope", "all");
        else if (roundFilter) params.set("round_id", roundFilter);
        const qs = params.toString();
        const url = `${API_URL}/playerspublic/rank-changes${qs ? `?${qs}` : ""}`;
        const response = await axios.get(url);
        if (isMounted) setData(response.data);
      } catch (err) {
        console.error("Error fetching rank changes:", err);
        if (isMounted) setData(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRankChanges();

    return () => {
      isMounted = false;
    };
  }, [roundFilter, seasonId, showAllInSeason]);

  const changes = useMemo(() => data?.changes || [], [data]);

  const serieOptions = useMemo(() => {
    const labels = new Map();
    changes.forEach((change) => {
      (change.series || []).forEach((serie) => {
        const label = getSerieLabel(serie.id, serie.name);
        const currentOrder = labels.get(label);
        const order = Number(serie.id) || 0;
        if (currentOrder === undefined || order < currentOrder) {
          labels.set(label, order);
        }
      });
    });
    return Array.from(labels, ([label, order]) => ({ label, order })).sort(
      (a, b) => a.order - b.order || a.label.localeCompare(b.label, "vi"),
    );
  }, [changes]);

  const teamOptions = useMemo(() => {
    const teams = new Set();
    changes
      .filter((change) => !serieFilter || getChangeSerieLabels(change).includes(serieFilter))
      .forEach((change) => {
        String(change.team_names || "Chưa có đội")
          .split(",")
          .map((team) => team.trim())
          .filter(Boolean)
          .forEach((team) => teams.add(team));
      });
    return Array.from(teams).sort((a, b) => a.localeCompare(b, "vi"));
  }, [changes, serieFilter]);

  useEffect(() => {
    if (teamFilter && !teamOptions.includes(teamFilter)) {
      setTeamFilter("");
    }
  }, [teamFilter, teamOptions]);

  const filteredChanges = useMemo(() => {
    const filtered = changes.filter((change) => {
      const matchesRound =
        !roundFilter ||
        (data?.scope === "all"
          ? String(change.round_id) === String(roundFilter)
          : true);
      const matchesTeam =
        !teamFilter ||
        String(change.team_names || "Chưa có đội")
          .split(",")
          .map((team) => team.trim())
          .includes(teamFilter);
      const matchesSerie = !serieFilter || getChangeSerieLabels(change).includes(serieFilter);
      const matchesReason = !reasonFilter || change.reason === reasonFilter;
      const matchesDirection = !directionFilter || change.direction === directionFilter;

      return matchesRound && matchesTeam && matchesSerie && matchesReason && matchesDirection;
    });

    return [...filtered].sort(
      (a, b) =>
        getFirstTeamName(a.team_names).localeCompare(getFirstTeamName(b.team_names), "vi") ||
        a.player_name.localeCompare(b.player_name, "vi"),
    );
  }, [changes, data, directionFilter, reasonFilter, roundFilter, serieFilter, teamFilter]);



  const summary = useMemo(() => {
    return changes.reduce(
      (acc, change) => {
        acc.teamNames.add(getFirstTeamName(change.team_names));
        if (change.direction === "up") acc.upCount += 1;
        if (change.direction === "down") acc.downCount += 1;
        if (change.reason === "point_based") acc.pointBasedCount += 1;
        if (change.reason === "bks_changed") acc.bksChangedCount += 1;
        return acc;
      },
      {
        teamNames: new Set(),
        upCount: 0,
        downCount: 0,
        pointBasedCount: 0,
        bksChangedCount: 0,
      },
    );
  }, [changes]);

  return (
    <section className={compact ? "client-section" : ""}>
      <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", overflow: "hidden" }}>
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <p className="client-kicker mb-1">Theo dõi đội</p>
              <h2 className="h4 fw-bold mb-1">
                {data?.scope === "all"
                  ? `VĐV thay đổi hạng trong ${data?.season?.name || "mùa này"}`
                  : `Thông báo thay đổi hạng sau vòng ${data?.round?.round_id || "-"}`}
              </h2>
              <small className="text-muted">
                {data?.scope === "all"
                  ? "Tổng kết toàn bộ các vòng của mùa đã chọn"
                  : `Các đội kiểm tra VĐV bị ảnh hưởng trong ${data?.season?.name || "mùa đang diễn ra"}`}
              </small>
            </div>
            <Badge bg="primary" className="px-3 py-2" style={{ borderRadius: "8px" }}>
              {filteredChanges.length} VĐV
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Đang tải thay đổi hạng...</span>
            </div>
          ) : data?.scope === "all" || (data?.round && changes.length > 0) ? (
            <>
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <div className="rank-change-summary">
                    <span>VĐV thay đổi</span>
                    <strong>{changes.length}</strong>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="rank-change-summary">
                    <span>Đội ảnh hưởng</span>
                    <strong>{summary.teamNames.size}</strong>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="rank-change-summary">
                    <span>Lên / xuống</span>
                    <strong>{summary.upCount} / {summary.downCount}</strong>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="rank-change-summary">
                    <span>Theo điểm / BKS</span>
                    <strong>{summary.pointBasedCount} / {summary.bksChangedCount}</strong>
                  </div>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6 col-lg-2">
                  <Form.Select value={roundFilter} onChange={(e) => setRoundFilter(e.target.value)}>
                    <option value="">Vòng mới nhất</option>
                    {(data?.availableRounds || []).map((r) => (
                      <option key={r.round_id} value={r.round_id}>
                        Vòng {r.round_id}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-6 col-lg-2">
                  <Form.Select value={serieFilter} onChange={(e) => setSerieFilter(e.target.value)}>
                    <option value="">Tất cả Serie</option>
                    {serieOptions.map((serie) => (
                      <option key={serie.label} value={serie.label}>
                        {serie.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-12 col-lg-3">
                  <Form.Select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
                    <option value="">Tất cả đội</option>
                    {teamOptions.map((team) => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </Form.Select>
                </div>
                <div className="col-6 col-lg-2">
                  <Form.Select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)}>
                    <option value="">Chiều</option>
                    <option value="up">Lên hạng</option>
                    <option value="down">Xuống hạng</option>
                  </Form.Select>
                </div>
                <div className="col-6 col-lg-3">
                  <Form.Select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)}>
                    <option value="">Lý do</option>
                    <option value="point_based">Theo điểm</option>
                    <option value="bks_changed">BKS thay đổi</option>
                  </Form.Select>
                </div>
              </div>

              {filteredChanges.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0 rank-change-table">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "30%" }}>Vận động viên</th>
                        <th style={{ width: "25%" }}>Đội</th>
                        <th style={{ width: "25%" }} className="text-center">Hạng</th>
                        <th style={{ width: "20%" }}>Chi tiết</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredChanges.map((change) => (
                        <tr key={change.id}>
                          <td>
                            <Link to={`/players/${change.player_id}`} className="text-decoration-none fw-medium">
                              {change.player_name}
                            </Link>
                          </td>
                          <td className="text-muted small">
                            {getFirstTeamName(change.team_names)}
                          </td>
                          <td className="text-center">
                            <span className={`${getLevelChipClass(change.old_level?.name)} me-1`}>
                              {change.old_level?.name || "-"}
                            </span>
                            <span className="mx-1 text-muted">{getDirectionIcon(change.direction)}</span>
                            <span className={getLevelChipClass(change.new_level?.name)}>
                              {change.new_level?.name || "-"}
                            </span>
                          </td>
                          <td>
                            <Badge bg={directionVariants[change.direction] || "secondary"} className="me-1" style={{ fontSize: "0.7rem" }}>
                              {directionLabels[change.direction]}
                            </Badge>
                            <span className="small text-muted">{change.reason_label}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: "12px" }}>
                  Không có VĐV phù hợp với bộ lọc hiện tại.
                </Alert>
              )}

              {(teamFilter || serieFilter || reasonFilter || directionFilter || roundFilter) && (
                <Button
                  variant="link"
                  className="px-0 mt-2"
                  onClick={() => {
                    setTeamFilter("");
                    setSerieFilter("");
                    setReasonFilter("");
                    setDirectionFilter("");
                    setRoundFilter("");
                  }}
                >
                  Xóa bộ lọc
                </Button>
              )}
            </>
          ) : (
            <Alert variant="info" className="mb-0 border-0" style={{ borderRadius: "12px" }}>
              Chưa có VĐV thay đổi hạng sau vòng này.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </section>
  );
};

export default RankChangesPanel;
